import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Clock, Info, Plus, Printer, Search, X } from 'lucide-react';
import { ordenarProdutosPalete } from './produtoPaleteData';
import {
  ACRESCIMO_TEMPO_PADRAO,
  META_PONTOS_DIA_PADRAO,
  MINUTOS_DIA_PADRAO,
  calcularAnaliseConjuntoEmbalar,
  calcularAnalisePontuacaoEmbalar,
  calcularRitmoEmbalar,
  chaveEntradaEmbalar,
  contarVolumesPalete,
  dividirPontuacaoPorVolumes,
  formatarDeltaMinutos,
  formatarHorasMinutos,
  legendaTempoExtra,
  minutosPorPecaEmbalar,
  normalizarNomeProdutoEmbalar,
  parseTempoParaSegundos,
  type AnaliseConjuntoEmbalar,
  type AnalisePontuacaoEmbalar,
  type ConfigTempoEmbalar,
  type RitmoEmbalar,
  type VereditoAnalisePontuacao,
} from '../../utils/tempoEmbalar';
import {
  carregarDadosTempoEmbalarCompleto,
  chaveBaseVolumeEmbalar,
  chaveMedicaoEmbalar,
  escolherMelhorDadosEmbalar,
  lerDadosTempoEmbalarLocalStorage,
  persistirDadosTempoEmbalar,
  type DadosPersistidosEmbalar,
  type EntradaVolumePersistida,
} from '../../utils/persistenciaTempoEmbalar';

const STORAGE_PRODUTOS_PALETE = 'produto-palete-config-ade';

interface PontuacaoRef {
  nome: string;
  m2: number;
  foraDeLinha?: boolean;
}

interface LinhaTabela {
  chave: string;
  chaveVolume: string;
  nome: string;
  nomeNorm: string;
  volume: number;
  medicao: number;
  totalMedicoes: number;
  totalVolumes: number;
  tempoStr: string;
  quantidadeStr: string;
  observacao: string;
  pontuacaoProduto: number | null;
  pontuacaoVolume: number | null;
  minutosPorPeca: number | null;
  ritmo: RitmoEmbalar | null;
  analise: AnalisePontuacaoEmbalar | null;
  analiseConjunto: AnaliseConjuntoEmbalar | null;
}

function lerVolumesPalete(): Map<string, number> {
  const mapa = new Map<string, number>();
  try {
    const raw = localStorage.getItem(STORAGE_PRODUTOS_PALETE);
    if (!raw) return mapa;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return mapa;
    for (const p of ordenarProdutosPalete(parsed)) {
      const nome = String(p.produto || '').trim();
      if (!nome) continue;
      mapa.set(normalizarNomeProdutoEmbalar(nome), contarVolumesPalete(p));
    }
  } catch {
    /* ignore */
  }
  return mapa;
}

function fmtNum(n: number, dec = 2): string {
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('pt-BR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: dec,
  });
}

function fmtInt(n: number): string {
  return Math.round(n).toLocaleString('pt-BR');
}

function badgeAnalise(veredito: VereditoAnalisePontuacao): string {
  switch (veredito) {
    case 'aumentar':
      return 'inline-block rounded-full px-2 py-0.5 text-[10px] font-bold bg-amber-100 text-amber-900 border border-amber-300';
    case 'diminuir':
      return 'inline-block rounded-full px-2 py-0.5 text-[10px] font-bold bg-emerald-100 text-emerald-900 border border-emerald-300';
    default:
      return 'inline-block rounded-full px-2 py-0.5 text-[10px] font-bold bg-neutral-100 text-neutral-700 border border-neutral-300';
  }
}

function textoVeredito(veredito: VereditoAnalisePontuacao): string {
  switch (veredito) {
    case 'aumentar':
      return 'Aumentar';
    case 'diminuir':
      return 'Diminuir';
    default:
      return 'OK';
  }
}

function escHtmlImpressao(s: string): string {
  return String(s || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function gerarHtmlImpressao(linhas: LinhaTabela[]): string {
  const rows = linhas
    .filter((l) => l.ritmo)
    .map(
      (l) => `
    <tr>
      <td>${escHtmlImpressao(l.nome)}${l.totalVolumes > 1 ? ` · Vol. ${l.volume}` : ''}${l.medicao > 1 ? ` · med. ${l.medicao}` : ''}</td>
      <td class="c">${l.totalVolumes > 1 ? l.volume : '—'}</td>
      <td>${escHtmlImpressao(l.observacao.trim()) || '—'}</td>
      <td class="c">${fmtNum(l.ritmo!.pecasPorSegundo, 3)}</td>
      <td class="c">${fmtNum(l.ritmo!.pecasPorMinuto, 2)}</td>
      <td class="c">${fmtInt(l.ritmo!.pecasPorHora)}</td>
      <td class="c">${fmtInt(l.ritmo!.pecasPorDia)}</td>
    </tr>`
    )
    .join('');

  return `<!DOCTYPE html>
<html lang="pt-BR"><head>
<meta charset="utf-8" />
<title>Tempo de Embalagem</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: Calibri, Arial, sans-serif; color: #000; margin: 16px; font-size: 11pt; }
  table { width: 100%; border-collapse: collapse; }
  th, td { border: 1px solid #333; padding: 5px 6px; text-align: left; vertical-align: middle; }
  td.c { text-align: center; white-space: nowrap; }
  thead tr th { background: #e8e8e8; font-size: 9pt; text-align: center; }
  thead tr th:first-child { text-align: left; }
  tbody tr:nth-child(even) { background: #f7f7f7; }
  @media print {
    @page { size: A4 landscape; margin: 10mm; }
    body { margin: 0; }
    thead { display: table-header-group; }
    tbody { display: table-row-group; }
    tr { page-break-inside: avoid; break-inside: avoid; }
  }
</style>
</head><body>
  <table>
    <thead>
      <tr>
        <th>Produto</th>
        <th>Vol.</th>
        <th>Observação</th>
        <th>Peças / segundo</th>
        <th>Peças / minuto</th>
        <th>Peças / hora</th>
        <th>Peças / dia</th>
      </tr>
    </thead>
    <tbody>
      ${rows || '<tr><td colspan="7">Nenhum produto com tempo e quantidade preenchidos.</td></tr>'}
    </tbody>
  </table>
</body></html>`;
}

interface TempoParaEmbalarProps {
  pontuacoesProdutos: PontuacaoRef[];
  ativo: boolean;
}

export default function TempoParaEmbalar({ pontuacoesProdutos, ativo }: TempoParaEmbalarProps) {
  const [dados, setDados] = useState<DadosPersistidosEmbalar>(lerDadosTempoEmbalarLocalStorage);
  const carregouPersistenciaRef = useRef(false);
  const dadosRef = useRef(dados);
  dadosRef.current = dados;
  const [paleteTick, setPaleteTick] = useState(0);
  const [filtroProduto, setFiltroProduto] = useState('');

  useEffect(() => {
    let cancelado = false;
    void carregarDadosTempoEmbalarCompleto().then((merged) => {
      if (cancelado) return;
      setDados((prev) => escolherMelhorDadosEmbalar(prev, merged));
      carregouPersistenciaRef.current = true;
    });
    return () => {
      cancelado = true;
    };
  }, []);

  useEffect(() => {
    if (!carregouPersistenciaRef.current) return;
    persistirDadosTempoEmbalar(dados);
  }, [dados]);

  useEffect(() => {
    const flush = () => {
      if (carregouPersistenciaRef.current) {
        persistirDadosTempoEmbalar(dadosRef.current);
      }
    };
    window.addEventListener('beforeunload', flush);
    window.addEventListener('pagehide', flush);
    return () => {
      flush();
      window.removeEventListener('beforeunload', flush);
      window.removeEventListener('pagehide', flush);
    };
  }, []);

  useEffect(() => {
    if (!ativo) return;
    setPaleteTick((t) => t + 1);
  }, [ativo, pontuacoesProdutos]);

  const volumesPalete = useMemo(() => {
    void paleteTick;
    return lerVolumesPalete();
  }, [paleteTick]);

  const produtosAtivos = useMemo(() => {
    const porNorm = new Map<string, PontuacaoRef>();
    for (const p of pontuacoesProdutos) {
      if (p.foraDeLinha || !String(p.nome || '').trim()) continue;
      const norm = normalizarNomeProdutoEmbalar(p.nome);
      const cur = porNorm.get(norm);
      if (!cur) {
        porNorm.set(norm, p);
        continue;
      }
      const m2 = Math.max(Number(cur.m2) || 0, Number(p.m2) || 0);
      porNorm.set(norm, { ...cur, m2: m2 || cur.m2 || p.m2 });
    }
    return [...porNorm.values()].sort((a, b) =>
      a.nome.trim().localeCompare(b.nome.trim(), 'pt-BR', {
        sensitivity: 'base',
        numeric: true,
      })
    );
  }, [pontuacoesProdutos]);

  const mapaPontuacao = useMemo(() => {
    const m = new Map<string, number>();
    for (const p of pontuacoesProdutos) {
      if (p.foraDeLinha) continue;
      const nome = String(p.nome || '').trim();
      if (!nome) continue;
      const pts = Number(p.m2);
      if (Number.isFinite(pts) && pts > 0) {
        m.set(normalizarNomeProdutoEmbalar(nome), pts);
      }
    }
    return m;
  }, [pontuacoesProdutos]);

  const linhas = useMemo((): LinhaTabela[] => {
    const out: LinhaTabela[] = [];
    const meta = dados.config.metaPontosDia ?? META_PONTOS_DIA_PADRAO;

    for (const p of produtosAtivos) {
      const nome = p.nome.trim();
      const nomeNorm = normalizarNomeProdutoEmbalar(nome);
      const pontuacaoAtual = mapaPontuacao.get(nomeNorm) ?? null;
      const totalVolumes =
        dados.volumesPorProduto[nomeNorm] ?? volumesPalete.get(nomeNorm) ?? 1;
      const vols = Math.min(3, Math.max(1, Math.round(totalVolumes)));
      const linhasProduto: LinhaTabela[] = [];

      for (let vol = 1; vol <= vols; vol += 1) {
        const chaveVolume = chaveEntradaEmbalar(nomeNorm, vol);
        const totalMedicoes = Math.min(
          20,
          Math.max(1, Math.round(dados.medicoesPorVolume?.[chaveVolume] ?? 1))
        );

        for (let med = 1; med <= totalMedicoes; med += 1) {
          const chave = chaveMedicaoEmbalar(nomeNorm, vol, med);
          const entrada = dados.entradas[chave] ?? { tempoStr: '', quantidadeStr: '' };
          const tempoSeg = parseTempoParaSegundos(entrada.tempoStr);
          const qtd = Number(String(entrada.quantidadeStr).replace(',', '.'));
          const ritmo =
            tempoSeg != null && Number.isFinite(qtd) && qtd > 0
              ? calcularRitmoEmbalar(tempoSeg, qtd, dados.config)
              : null;
          const minPorPeca =
            ritmo != null ? minutosPorPecaEmbalar(ritmo.tempoAjustadoSeg, qtd) : null;

          const obsLista = dados.observacoesPorVolume?.[chave];
          let observacao = '';
          if (Array.isArray(obsLista) && obsLista.length > 0) {
            observacao = String(obsLista[0] ?? '');
          } else if (
            med === 1 &&
            vol === 1 &&
            String(dados.observacoesPorProduto?.[nomeNorm] || '').trim()
          ) {
            observacao = String(dados.observacoesPorProduto[nomeNorm]);
          }

          linhasProduto.push({
            chave,
            chaveVolume,
            nome,
            nomeNorm,
            volume: vol,
            medicao: med,
            totalMedicoes,
            totalVolumes: vols,
            tempoStr: entrada.tempoStr,
            quantidadeStr: entrada.quantidadeStr,
            observacao,
            pontuacaoProduto: pontuacaoAtual,
            pontuacaoVolume: null,
            minutosPorPeca: minPorPeca,
            ritmo,
            analise: null,
            analiseConjunto: null,
          });
        }
      }

      /** Só a 1ª medição de cada volume entra no rateio/conjunto (lógica oficial intacta). */
      const linhasPrincipais = linhasProduto.filter((l) => l.medicao === 1);

      const volumesComTempo = linhasPrincipais
        .filter((l) => l.minutosPorPeca != null && l.minutosPorPeca > 0)
        .map((l) => ({ volume: l.volume, minutosPorPeca: l.minutosPorPeca! }));

      const mapaPontuacaoVol =
        pontuacaoAtual != null && volumesComTempo.length > 0
          ? vols === 1
            ? new Map([[1, pontuacaoAtual]])
            : dividirPontuacaoPorVolumes(pontuacaoAtual, volumesComTempo)
          : null;

      const pecasPorDiaVolumes = linhasPrincipais
        .filter((l) => l.ritmo != null)
        .map((l) => l.ritmo!.pecasPorDia);
      const conjuntoCompleto =
        pontuacaoAtual != null &&
        pecasPorDiaVolumes.length === vols &&
        pecasPorDiaVolumes.length > 0
          ? calcularAnaliseConjuntoEmbalar(dados.config, pontuacaoAtual, pecasPorDiaVolumes)
          : null;

      for (const linha of linhasProduto) {
        const pi =
          vols === 1
            ? pontuacaoAtual
            : mapaPontuacaoVol?.get(linha.volume) ?? null;
        linha.pontuacaoVolume = pi;
        linha.analise =
          linha.ritmo && pi != null
            ? calcularAnalisePontuacaoEmbalar(meta, pi, linha.ritmo.pecasPorDia)
            : null;
        linha.analiseConjunto = conjuntoCompleto;
      }

      out.push(...linhasProduto);
    }
    return out;
  }, [produtosAtivos, dados, volumesPalete, mapaPontuacao]);

  const setConfig = useCallback((patch: Partial<ConfigTempoEmbalar>) => {
    setDados((prev) => {
      const next = {
        ...prev,
        config: { ...prev.config, ...patch },
      };
      if (carregouPersistenciaRef.current) {
        persistirDadosTempoEmbalar(next);
      }
      return next;
    });
  }, []);

  const setVolumesProduto = useCallback((nomeNorm: string, volumes: number) => {
    const vols = Math.min(3, Math.max(1, Math.round(volumes)));
    setDados((prev) => {
      const next = {
        ...prev,
        volumesPorProduto: { ...prev.volumesPorProduto, [nomeNorm]: vols },
      };
      if (carregouPersistenciaRef.current) {
        persistirDadosTempoEmbalar(next);
      }
      return next;
    });
  }, []);

  const setEntrada = useCallback((chave: string, patch: Partial<EntradaVolumePersistida>) => {
    setDados((prev) => {
      const next: DadosPersistidosEmbalar = {
        ...prev,
        entradas: {
          ...prev.entradas,
          [chave]: {
            tempoStr: '',
            quantidadeStr: '',
            ...prev.entradas[chave],
            ...patch,
          },
        },
      };
      if (carregouPersistenciaRef.current) {
        persistirDadosTempoEmbalar(next);
      }
      return next;
    });
  }, []);

  const setObservacaoLinha = useCallback((chave: string, texto: string) => {
    setDados((prev) => {
      const observacoesPorVolume = {
        ...(prev.observacoesPorVolume || {}),
        [chave]: [texto],
      };
      const base = chaveBaseVolumeEmbalar(chave);
      const sep = base.lastIndexOf('|');
      const nomeNorm = sep > 0 ? base.slice(0, sep) : base;
      const vol = sep > 0 ? base.slice(sep + 1) : '1';
      const ehPrincipalVol1 = !chave.includes('#') && vol === '1';
      let observacoesPorProduto = { ...(prev.observacoesPorProduto || {}) };
      if (ehPrincipalVol1) {
        if (String(texto).trim()) observacoesPorProduto[nomeNorm] = texto;
        else {
          const { [nomeNorm]: _rem, ...resto } = observacoesPorProduto;
          observacoesPorProduto = resto;
        }
      }
      const next: DadosPersistidosEmbalar = {
        ...prev,
        observacoesPorVolume,
        observacoesPorProduto,
      };
      if (carregouPersistenciaRef.current) {
        persistirDadosTempoEmbalar(next);
      }
      return next;
    });
  }, []);

  const adicionarLinhaMedicao = useCallback((nomeNorm: string, volume: number) => {
    setDados((prev) => {
      const chaveVolume = chaveEntradaEmbalar(nomeNorm, volume);
      const atual = Math.min(20, Math.max(1, Math.round(prev.medicoesPorVolume?.[chaveVolume] ?? 1)));
      if (atual >= 20) return prev;
      const nova = atual + 1;
      const chaveNova = chaveMedicaoEmbalar(nomeNorm, volume, nova);
      const next: DadosPersistidosEmbalar = {
        ...prev,
        medicoesPorVolume: { ...(prev.medicoesPorVolume || {}), [chaveVolume]: nova },
        entradas: {
          ...prev.entradas,
          [chaveNova]: prev.entradas[chaveNova] ?? { tempoStr: '', quantidadeStr: '' },
        },
        observacoesPorVolume: {
          ...(prev.observacoesPorVolume || {}),
          [chaveNova]: prev.observacoesPorVolume?.[chaveNova] ?? [''],
        },
      };
      if (carregouPersistenciaRef.current) {
        persistirDadosTempoEmbalar(next);
      }
      return next;
    });
  }, []);

  const removerLinhaMedicao = useCallback(
    (nomeNorm: string, volume: number, medicao: number) => {
      if (medicao <= 1) return;
      setDados((prev) => {
        const chaveVolume = chaveEntradaEmbalar(nomeNorm, volume);
        const total = Math.min(
          20,
          Math.max(1, Math.round(prev.medicoesPorVolume?.[chaveVolume] ?? 1))
        );
        if (medicao > total) return prev;

        const entradas = { ...prev.entradas };
        const observacoesPorVolume = { ...(prev.observacoesPorVolume || {}) };

        for (let m = medicao; m < total; m += 1) {
          const dest = chaveMedicaoEmbalar(nomeNorm, volume, m);
          const src = chaveMedicaoEmbalar(nomeNorm, volume, m + 1);
          if (entradas[src]) entradas[dest] = entradas[src];
          else delete entradas[dest];
          if (observacoesPorVolume[src]) observacoesPorVolume[dest] = observacoesPorVolume[src];
          else delete observacoesPorVolume[dest];
        }
        const ultima = chaveMedicaoEmbalar(nomeNorm, volume, total);
        delete entradas[ultima];
        delete observacoesPorVolume[ultima];

        const novoTotal = total - 1;
        const medicoesPorVolume = { ...(prev.medicoesPorVolume || {}) };
        if (novoTotal <= 1) delete medicoesPorVolume[chaveVolume];
        else medicoesPorVolume[chaveVolume] = novoTotal;

        const next: DadosPersistidosEmbalar = {
          ...prev,
          entradas,
          observacoesPorVolume,
          medicoesPorVolume,
        };
        if (carregouPersistenciaRef.current) {
          persistirDadosTempoEmbalar(next);
        }
        return next;
      });
    },
    []
  );

  const { config } = dados;

  const linhasComCalculo = linhas.filter((l) => l.ritmo);

  const linhasFiltradas = useMemo(() => {
    const q = filtroProduto.trim();
    if (!q) return linhas;
    const normQ = normalizarNomeProdutoEmbalar(q);
    const produtosOk = new Set<string>();
    for (const l of linhas) {
      if (l.nomeNorm.includes(normQ)) produtosOk.add(l.nomeNorm);
    }
    return linhas.filter((l) => produtosOk.has(l.nomeNorm));
  }, [linhas, filtroProduto]);

  const filtroAtivo = filtroProduto.trim().length > 0;

  /** rowSpan do conjunto: 1ª linha do produto, cobrindo todas as linhas visíveis dele. */
  const linhasVisiveisPorProduto = useMemo(() => {
    const m = new Map<string, number>();
    for (const l of linhasFiltradas) {
      m.set(l.nomeNorm, (m.get(l.nomeNorm) ?? 0) + 1);
    }
    return m;
  }, [linhasFiltradas]);

  const principaisVisiveisPorProduto = useMemo(() => {
    const m = new Map<string, number>();
    for (const l of linhasFiltradas) {
      if (l.medicao === 1) m.set(l.nomeNorm, (m.get(l.nomeNorm) ?? 0) + 1);
    }
    return m;
  }, [linhasFiltradas]);

  /** Exemplo fixo na observação: 00:01:11,97 → 10 produtos */
  const exemploCalc = useMemo(() => {
    const tempoSeg = 60 + 11.97;
    const qtd = 10;
    const acrescimo = config.percentualAcrescimoTempo;
    const fator = 1 + acrescimo / 100;
    const tempoAjustadoSeg = tempoSeg * fator;
    const tempoAjustadoMin = tempoAjustadoSeg / 60;
    const porMin = qtd / tempoAjustadoMin;
    return {
      tempoSeg,
      qtd,
      fator,
      tempoAjustadoSeg,
      tempoAjustadoMin,
      porMin,
      porH: porMin * 60,
      porDia: porMin * config.minutosDia,
    };
  }, [config.minutosDia, config.percentualAcrescimoTempo]);

  const imprimir = () => {
    const html = gerarHtmlImpressao(linhas);
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(() => {
      win.print();
      win.close();
    }, 300);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-semibold text-black flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Tempo de Embalagem
          </h3>
          <p className="text-sm text-neutral-600 mt-1 max-w-3xl">
            Cronometragem por produto/volume e <strong>análise de pontuação</strong> (m² da aba
            Pontuação) para saber se pode aumentar ou diminuir pontos ao rebalancear a produção.
          </p>
        </div>
        <button
          type="button"
          onClick={imprimir}
          disabled={linhasComCalculo.length === 0}
          className="px-4 py-2 ade-btn-gold relative z-10 text-sm font-semibold rounded-lg inline-flex items-center gap-1.5 disabled:opacity-40"
        >
          <Printer className="w-4 h-4" />
          Imprimir relatório
        </button>
      </div>

      <div className="rounded-xl border border-[#009b3a]/20 bg-[#f0faf4] p-4 text-sm text-neutral-800">
        <div className="flex items-start gap-2">
          <Info className="w-4 h-4 shrink-0 text-[#009b3a] mt-0.5" />
          <div className="space-y-3 w-full">
            <div>
              <p className="font-semibold text-black">Como calcular na calculadora</p>
              <p className="text-xs sm:text-sm text-neutral-600 mt-1">
                Exemplo: você cronometrou <strong className="text-black">00:01:11,97</strong> para
                embalar <strong className="text-black">10</strong> produtos. O app faz estes passos
                (com <strong className="text-black">{config.percentualAcrescimoTempo}%</strong> a
                mais no tempo e jornada de{' '}
                <strong className="text-black">
                  {config.minutosDia} min ({formatarHorasMinutos(config.minutosDia)})
                </strong>
                ):
              </p>
            </div>

            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              <div className="rounded-lg border border-[#009b3a]/15 bg-white/80 px-3 py-2">
                <p className="text-[10px] font-bold uppercase tracking-wide text-[#009b3a]">
                  Passo 1 · Tempo em segundos
                </p>
                <p className="text-xs mt-1 font-mono">
                  1 min = 60 s → 60 + 11,97 ={' '}
                  <strong>{fmtNum(exemploCalc.tempoSeg, 2)} s</strong>
                </p>
              </div>
              <div className="rounded-lg border border-[#009b3a]/15 bg-white/80 px-3 py-2">
                <p className="text-[10px] font-bold uppercase tracking-wide text-[#009b3a]">
                  Passo 2 · Somar {config.percentualAcrescimoTempo}% no tempo
                </p>
                <p className="text-xs mt-1 font-mono">
                  {fmtNum(exemploCalc.tempoSeg, 2)} × {fmtNum(exemploCalc.fator, 2)} ={' '}
                  <strong>{fmtNum(exemploCalc.tempoAjustadoSeg, 2)} s</strong>
                </p>
              </div>
              <div className="rounded-lg border border-[#009b3a]/15 bg-white/80 px-3 py-2">
                <p className="text-[10px] font-bold uppercase tracking-wide text-[#009b3a]">
                  Passo 3 · Tempo em minutos
                </p>
                <p className="text-xs mt-1 font-mono">
                  {fmtNum(exemploCalc.tempoAjustadoSeg, 2)} ÷ 60 ={' '}
                  <strong>{fmtNum(exemploCalc.tempoAjustadoMin, 2)} min</strong>
                </p>
              </div>
              <div className="rounded-lg border border-[#009b3a]/15 bg-white/80 px-3 py-2">
                <p className="text-[10px] font-bold uppercase tracking-wide text-[#009b3a]">
                  Passo 4 · Peças por minuto
                </p>
                <p className="text-xs mt-1 font-mono">
                  {exemploCalc.qtd} ÷ {fmtNum(exemploCalc.tempoAjustadoMin, 2)} ={' '}
                  <strong>{fmtNum(exemploCalc.porMin, 2)} peças/min</strong>
                </p>
              </div>
              <div className="rounded-lg border border-[#009b3a]/15 bg-white/80 px-3 py-2">
                <p className="text-[10px] font-bold uppercase tracking-wide text-[#009b3a]">
                  Passo 5 · Peças por hora
                </p>
                <p className="text-xs mt-1 font-mono">
                  {fmtNum(exemploCalc.porMin, 2)} × 60 ={' '}
                  <strong>{fmtInt(exemploCalc.porH)} peças/h</strong>
                </p>
              </div>
              <div className="rounded-lg border border-[#009b3a]/15 bg-white/80 px-3 py-2">
                <p className="text-[10px] font-bold uppercase tracking-wide text-[#009b3a]">
                  Passo 6 · Peças no dia todo
                </p>
                <p className="text-xs mt-1 font-mono">
                  {fmtNum(exemploCalc.porMin, 2)} × {config.minutosDia} ={' '}
                  <strong>{fmtInt(exemploCalc.porDia)} peças/dia</strong>
                </p>
              </div>
            </div>

            <p className="text-xs text-neutral-600 border-t border-[#009b3a]/10 pt-2">
              <strong className="text-black">Análise — 1 volume:</strong> pontuação do produto ={' '}
              <em>P</em>. Peças/dia esperadas = {fmtInt(config.metaPontosDia)} ÷ <em>P</em>.
              Pelo tempo medido → peças/dia reais → pontuação sugerida ={' '}
              {fmtInt(config.metaPontosDia)} ÷ peças/dia reais.{' '}
              <strong>Aumentar</strong> = mais lento que o cadastro · <strong>Diminuir</strong> =
              mais rápido (libera pontos).
            </p>
            <p className="text-xs text-neutral-600 pt-1">
              <strong className="text-black">Análise — 2 ou 3 volumes:</strong> divide{' '}
              <em>P</em> entre os volumes pelo tempo de cada caixa:{' '}
              <em>
                min/peça = (tempo ajustado em min) ÷ quantidade · P_vol = P × (min_vol ÷ soma dos
                min)
              </em>
              . Ex.: <em>P</em>=2, Vol.1=1 min/peça, Vol.2=2 min, Vol.3=3 min → soma 6 min →
              P₁=0,33 · P₂=0,67 · P₃=1,33 (soma = 2). Cada volume compara sua fatia{' '}
              <em>P_vol</em> com o ritmo medido daquele volume.
            </p>
            <p className="text-xs text-neutral-600 pt-1">
              <strong className="text-black">Conjunto — tempo para meta cadastrada:</strong> some as{' '}
              <em>Pont. sugerida</em> dos volumes → <em>P ideal</em>. Para cumprir a meta do cadastro
              (ex.: 800 produtos com P=2) no ritmo medido:{' '}
              <em>tempo extra = jornada × (P ideal ÷ P cadastro − 1)</em>. Ex.: VIGO P=2 → ideal 2,38 →
              +1h40 além de 528 min (<strong className="text-amber-800">Excede — ruim</strong>). Se embalar{' '}
              <strong className="text-black">mais rápido</strong> que o cadastro, o valor fica{' '}
              <strong className="text-emerald-700">negativo</strong> —{' '}
              <strong className="text-emerald-700">Sobra tempo (bom)</strong>.
            </p>
            <p className="text-xs text-neutral-600 pt-1">
              <strong className="text-black">Ritmo:</strong> tempo medido → segundos → soma{' '}
              {config.percentualAcrescimoTempo}% → minutos → quantidade ÷ tempo → × 60 (h) e ×{' '}
              {config.minutosDia} (dia). Cada volume tem tempo e quantidade próprios.
            </p>
          </div>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5 bg-[#fafbfc] rounded-xl p-4 border border-[#009b3a]/15">
        <div>
          <label className="block text-xs font-medium text-black mb-1">Meta do dia (pontos)</label>
          <input
            type="number"
            min={1}
            step={1}
            value={config.metaPontosDia}
            onChange={(e) =>
              setConfig({
                metaPontosDia: Math.max(1, Number(e.target.value) || META_PONTOS_DIA_PADRAO),
              })
            }
            className="w-full px-3 py-2 rounded-lg border border-[#009b3a]/22 text-sm"
          />
          <p className="text-[10px] text-neutral-500 mt-1">Padrão {META_PONTOS_DIA_PADRAO} pontos</p>
        </div>
        <div>
          <label className="block text-xs font-medium text-black mb-1">Jornada (minutos)</label>
          <input
            type="number"
            min={1}
            step={1}
            value={config.minutosDia}
            onChange={(e) =>
              setConfig({
                minutosDia: Math.max(1, Number(e.target.value) || MINUTOS_DIA_PADRAO),
              })
            }
            className="w-full px-3 py-2 rounded-lg border border-[#009b3a]/22 text-sm"
          />
          <p className="text-[10px] text-neutral-500 mt-1">
            Padrão 528 min = {formatarHorasMinutos(528)} · Atual:{' '}
            {formatarHorasMinutos(config.minutosDia)}
          </p>
        </div>
        <div>
          <label className="block text-xs font-medium text-black mb-1">
            Acréscimo no tempo (%)
          </label>
          <input
            type="number"
            min={0}
            step={1}
            value={config.percentualAcrescimoTempo}
            onChange={(e) => {
              const v = Number(e.target.value);
              setConfig({
                percentualAcrescimoTempo:
                  Number.isFinite(v) && v >= 0 ? v : ACRESCIMO_TEMPO_PADRAO,
              });
            }}
            className="w-full px-3 py-2 rounded-lg border border-[#009b3a]/22 text-sm"
          />
          <p className="text-[10px] text-neutral-500 mt-1">
            Troca/paradas · tempo × {(1 + config.percentualAcrescimoTempo / 100).toFixed(2)}
          </p>
        </div>
        <div className="text-sm text-neutral-700 sm:col-span-1 flex flex-col justify-center">
          <p>
            <strong className="text-black">{produtosAtivos.length}</strong> produto(s) ·{' '}
            <strong className="text-black">{linhasComCalculo.length}</strong> com tempo
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 pointer-events-none" />
          <input
            type="search"
            value={filtroProduto}
            onChange={(e) => setFiltroProduto(e.target.value)}
            placeholder="Buscar produto..."
            className="w-full pl-9 pr-9 py-2 rounded-lg border border-[#009b3a]/22 text-sm bg-white"
          />
          {filtroAtivo && (
            <button
              type="button"
              onClick={() => setFiltroProduto('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-neutral-500 hover:text-black hover:bg-neutral-100"
              title="Limpar busca"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        {filtroAtivo && (
          <p className="text-xs text-neutral-600">
            <strong className="text-black">{linhasFiltradas.length}</strong> de{' '}
            <strong className="text-black">{linhas.length}</strong> linha(s)
          </p>
        )}
      </div>

      <div className="bg-[#eef0f4] rounded-xl border border-[#009b3a]/15 overflow-auto max-h-[calc(100vh-380px)]">
        <table className="w-full text-sm min-w-[1880px]">
          <thead className="sticky top-0 bg-[#e4e6ec] z-10">
            <tr className="text-left text-neutral-700 border-b border-[#009b3a]/20">
              <th className="px-3 py-2 font-semibold min-w-[160px]">Produto</th>
              <th className="px-3 py-2 font-semibold text-center w-20">Volumes</th>
              <th className="px-3 py-2 font-semibold text-center min-w-[110px]">Tempo</th>
              <th className="px-3 py-2 font-semibold text-center w-16">Qtd</th>
              <th className="px-3 py-2 font-semibold min-w-[120px]">Observação</th>
              <th className="px-3 py-2 font-semibold text-center w-22">Peças / seg</th>
              <th className="px-3 py-2 font-semibold text-center w-22">Peças / min</th>
              <th className="px-3 py-2 font-semibold text-center w-22">Peças / h</th>
              <th className="px-3 py-2 font-semibold text-center w-24">Peças / dia</th>
              <th
                className="px-3 py-2 font-semibold text-center border-l border-[#009b3a]/25 text-[#007a2e]"
                colSpan={6}
              >
                Análise por volume
              </th>
              <th
                className="px-3 py-2 font-semibold text-center border-l border-amber-500/35 text-amber-900"
                colSpan={5}
              >
                Conjunto — meta do cadastro no dia
              </th>
            </tr>
            <tr className="text-left text-[10px] text-neutral-600 border-b border-[#009b3a]/20 bg-[#e4e6ec]">
              <th colSpan={9} />
              <th className="px-2 py-1 text-center font-medium border-l border-[#009b3a]/25">Pont. prod.</th>
              <th className="px-2 py-1 text-center font-medium">Pont. vol.</th>
              <th className="px-2 py-1 text-center font-medium">Min/peça</th>
              <th className="px-2 py-1 text-center font-medium">Peças/dia (pontos)</th>
              <th className="px-2 py-1 text-center font-medium">Pont. sugerida</th>
              <th className="px-2 py-1 text-center font-medium">Análise</th>
              <th className="px-2 py-1 text-center font-medium border-l border-amber-500/30">P ideal</th>
              <th className="px-2 py-1 text-center font-medium">Meta prod./dia</th>
              <th className="px-2 py-1 text-center font-medium">Ritmo prod./dia</th>
              <th className="px-2 py-1 text-center font-medium">Δ Tempo</th>
              <th className="px-2 py-1 text-center font-medium">Tempo p/ meta</th>
            </tr>
          </thead>
          <tbody>
            {linhasFiltradas.length === 0 ? (
              <tr>
                <td colSpan={20} className="px-3 py-8 text-center text-neutral-600">
                  {filtroAtivo
                    ? `Nenhum produto encontrado para "${filtroProduto.trim()}".`
                    : 'Nenhum produto na Pontuação (ativos).'}
                </td>
              </tr>
            ) : (
              linhasFiltradas.map((l, idx) => {
                const primeiraDoProduto =
                  idx === 0 || linhasFiltradas[idx - 1].nomeNorm !== l.nomeNorm;
                const principaisOk =
                  (principaisVisiveisPorProduto.get(l.nomeNorm) ?? 0) === l.totalVolumes;
                const conjuntoRowSpan =
                  primeiraDoProduto && principaisOk
                    ? linhasVisiveisPorProduto.get(l.nomeNorm) ?? 0
                    : 0;
                return (
                <tr
                  key={`${l.chave}|${idx}`}
                  className="border-b border-[#009b3a]/10 odd:bg-[#fafbfc] even:bg-[#eef0f4]"
                >
                  <td className="px-3 py-2 font-medium text-black whitespace-nowrap">
                    {l.nome}
                    {l.totalVolumes > 1 && (
                      <span className="text-neutral-500 font-normal text-xs ml-1">
                        · Vol. {l.volume}
                      </span>
                    )}
                    {l.medicao > 1 && (
                      <span className="text-neutral-500 font-normal text-xs ml-1">
                        · med. {l.medicao}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-center">
                    {l.volume === 1 && l.medicao === 1 ? (
                      <select
                        value={l.totalVolumes}
                        onChange={(e) =>
                          setVolumesProduto(l.nomeNorm, Number(e.target.value))
                        }
                        className="w-14 px-1 py-1 rounded border border-[#009b3a]/22 text-sm text-center bg-white"
                        title="Quantidade de volumes deste produto"
                      >
                        <option value={1}>1</option>
                        <option value={2}>2</option>
                        <option value={3}>3</option>
                      </select>
                    ) : (
                      <span className="text-neutral-500 text-xs">—</span>
                    )}
                  </td>
                  <td className="px-2 py-1">
                    <input
                      type="text"
                      value={l.tempoStr}
                      onChange={(e) => setEntrada(l.chave, { tempoStr: e.target.value })}
                      placeholder="00:01:01,17"
                      className="w-full min-w-[110px] px-2 py-1.5 rounded border border-[#009b3a]/22 text-sm tabular-nums text-center bg-white"
                      spellCheck={false}
                    />
                  </td>
                  <td className="px-2 py-1">
                    <input
                      type="text"
                      inputMode="decimal"
                      value={l.quantidadeStr}
                      onChange={(e) => setEntrada(l.chave, { quantidadeStr: e.target.value })}
                      placeholder="10"
                      className="w-full px-2 py-1.5 rounded border border-[#009b3a]/22 text-sm tabular-nums text-center bg-white"
                    />
                  </td>
                  <td className="px-2 py-1 align-top">
                    <div className="flex flex-col gap-1 min-w-[140px]">
                      <div className="flex items-center gap-1">
                        <input
                          type="text"
                          value={l.observacao}
                          onChange={(e) => setObservacaoLinha(l.chave, e.target.value)}
                          placeholder={
                            l.totalVolumes > 1
                              ? `Obs. Vol. ${l.volume}...`
                              : 'Detalhes, lembretes...'
                          }
                          className="w-full min-w-[100px] px-2 py-1.5 rounded border border-[#009b3a]/22 text-sm bg-white"
                          title={`Observação desta linha (Vol. ${l.volume})`}
                        />
                        {l.medicao > 1 && (
                          <button
                            type="button"
                            onClick={() =>
                              removerLinhaMedicao(l.nomeNorm, l.volume, l.medicao)
                            }
                            className="shrink-0 p-1 rounded text-neutral-400 hover:text-red-600 hover:bg-red-50"
                            title="Remover esta linha de medição"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                      {l.medicao === l.totalMedicoes && (
                        <button
                          type="button"
                          onClick={() => adicionarLinhaMedicao(l.nomeNorm, l.volume)}
                          className="inline-flex items-center gap-1 self-start text-[10px] font-semibold text-[#007a2e] hover:text-[#005f24] px-1 py-0.5 rounded hover:bg-[#e8f7ee]"
                          title="Adicionar outra linha completa (tempo, qtd e observação) neste volume"
                        >
                          <Plus className="w-3 h-3" />
                          Linha
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-center tabular-nums text-neutral-800">
                    {l.ritmo ? fmtNum(l.ritmo.pecasPorSegundo, 3) : '—'}
                  </td>
                  <td className="px-3 py-2 text-center tabular-nums text-neutral-800">
                    {l.ritmo ? fmtNum(l.ritmo.pecasPorMinuto, 2) : '—'}
                  </td>
                  <td className="px-3 py-2 text-center font-bold text-[#009b3a] tabular-nums">
                    {l.ritmo ? fmtInt(l.ritmo.pecasPorHora) : '—'}
                  </td>
                  <td className="px-3 py-2 text-center tabular-nums">
                    {l.ritmo ? fmtInt(l.ritmo.pecasPorDia) : '—'}
                  </td>
                  <td className="px-2 py-2 text-center tabular-nums text-black border-l border-[#009b3a]/15">
                    {l.volume === 1 || l.totalVolumes === 1
                      ? l.pontuacaoProduto != null
                        ? l.pontuacaoProduto.toFixed(2)
                        : '—'
                      : l.pontuacaoProduto != null
                        ? l.pontuacaoProduto.toFixed(2)
                        : '—'}
                  </td>
                  <td className="px-2 py-2 text-center tabular-nums font-medium text-black">
                    {l.pontuacaoVolume != null ? fmtNum(l.pontuacaoVolume, 3) : '—'}
                  </td>
                  <td className="px-2 py-2 text-center tabular-nums text-neutral-600">
                    {l.minutosPorPeca != null ? fmtNum(l.minutosPorPeca, 3) : '—'}
                  </td>
                  <td className="px-2 py-2 text-center tabular-nums text-black">
                    {l.analise ? fmtInt(l.analise.pecasDiaTeorica) : '—'}
                  </td>
                  <td className="px-2 py-2 text-center tabular-nums font-semibold text-[#007a2e]">
                    {l.analise ? fmtNum(l.analise.pontuacaoSugerida, 3) : '—'}
                  </td>
                  <td className="px-2 py-2 text-center">
                    {l.analise ? (
                      <div className="flex flex-col items-center gap-0.5">
                        <span className={badgeAnalise(l.analise.veredito)}>
                          {textoVeredito(l.analise.veredito)}
                        </span>
                        <span className="text-[9px] text-neutral-500 tabular-nums">
                          {l.analise.diferenca >= 0 ? '+' : ''}
                          {fmtNum(l.analise.diferenca, 3)}
                        </span>
                        {l.totalVolumes > 1 && l.pontuacaoVolume != null && (
                          <span className="text-[9px] text-neutral-500 leading-tight">
                            fatia de {fmtNum(l.pontuacaoVolume, 3)} pts
                          </span>
                        )}
                      </div>
                    ) : (
                      '—'
                    )}
                  </td>
                  {conjuntoRowSpan > 0 ? (
                    <>
                      <td
                        rowSpan={conjuntoRowSpan}
                        className="px-2 py-2 text-center tabular-nums font-bold text-[#007a2e] border-l border-amber-500/25 align-middle"
                      >
                        {l.analiseConjunto ? (
                          <div className="flex flex-col items-center gap-0.5">
                            <span>{fmtNum(l.analiseConjunto.pontuacaoSugerida, 2)}</span>
                            {Math.abs(l.analiseConjunto.diferencaPontuacao) >= 0.005 && (
                              <span
                                className={`text-[9px] font-semibold ${
                                  l.analiseConjunto.diferencaPontuacao > 0
                                    ? 'text-amber-700'
                                    : 'text-emerald-700'
                                }`}
                              >
                                {l.analiseConjunto.diferencaPontuacao >= 0 ? '+' : ''}
                                {fmtNum(l.analiseConjunto.diferencaPontuacao, 2)} pts
                              </span>
                            )}
                          </div>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td
                        rowSpan={conjuntoRowSpan}
                        className="px-2 py-2 text-center tabular-nums text-black align-middle"
                        title="Produtos completos/dia que o cadastro promete"
                      >
                        {l.analiseConjunto ? fmtInt(l.analiseConjunto.produtosPlanejadosDia) : '—'}
                      </td>
                      <td
                        rowSpan={conjuntoRowSpan}
                        className="px-2 py-2 text-center tabular-nums text-black align-middle"
                        title="Produtos completos/dia no ritmo medido (na jornada)"
                      >
                        {l.analiseConjunto ? fmtInt(l.analiseConjunto.produtosNoRitmoDia) : '—'}
                      </td>
                      <td
                        rowSpan={conjuntoRowSpan}
                        className="px-2 py-2 text-center tabular-nums align-middle"
                        title={`Além dos ${l.analiseConjunto?.minutosJornada ?? config.minutosDia} min da jornada`}
                      >
                        {l.analiseConjunto ? (
                          (() => {
                            const leg = legendaTempoExtra(l.analiseConjunto.minutosExtra);
                            return (
                              <div className="flex flex-col items-center gap-0.5">
                                <span
                                  className={
                                    leg.tipo === 'excede'
                                      ? 'font-bold text-amber-800'
                                      : leg.tipo === 'sobra'
                                        ? 'font-semibold text-emerald-700'
                                        : 'text-neutral-600'
                                  }
                                  title={leg.descricao}
                                >
                                  {formatarDeltaMinutos(l.analiseConjunto.minutosExtra)}
                                </span>
                                <span
                                  className={`text-[9px] font-bold uppercase tracking-wide ${
                                    leg.tipo === 'excede'
                                      ? 'text-red-700'
                                      : leg.tipo === 'sobra'
                                        ? 'text-emerald-700'
                                        : 'text-neutral-500'
                                  }`}
                                >
                                  {leg.rotulo}
                                </span>
                              </div>
                            );
                          })()
                        ) : (
                          '—'
                        )}
                      </td>
                      <td
                        rowSpan={conjuntoRowSpan}
                        className="px-2 py-2 text-center tabular-nums font-semibold text-black align-middle"
                        title="Tempo total para embalar a meta do cadastro no ritmo atual"
                      >
                        {l.analiseConjunto
                          ? formatarHorasMinutos(l.analiseConjunto.minutosTotal)
                          : '—'}
                      </td>
                    </>
                  ) : null}
                </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
